import type { ComponentType } from 'react'
import { memo, useEffect } from 'react'

import { AuthService } from '@space-shell/common/services/auth'
import { IS_CUSTOM_DOMAIN } from '@space-shell/constant/domain'
import { RouteMode, useAppStore } from '@space-shell/core/store'
import { useLocation } from 'react-router-dom'

type withAuthOptions = {
  isPrivate?: boolean
}

export const withAuth = <T extends Record<never, unknown>>(
  WrappedComponent: ComponentType<T>,
  options: withAuthOptions = { isPrivate: false }
) => {
  const MemoizedComponent = memo(WrappedComponent) as unknown as ComponentType<T>

  return (props: T) => {
    const { lastRouteMode, setLastRouteMode } = useAppStore()
    const location = useLocation()

    useEffect(() => {
      const { isPrivate: isPrivateRoute } = options
      const currentRouteMode = isPrivateRoute ? RouteMode.PRIVATE : RouteMode.HYBRID
      const isAuthRoute = location?.pathname?.includes('/auth')

      if (IS_CUSTOM_DOMAIN && !isPrivateRoute && !isAuthRoute) {
        return
      }

      if (!lastRouteMode || lastRouteMode !== currentRouteMode) {
        setLastRouteMode(currentRouteMode)
        AuthService.start({ signInRedirect: isPrivateRoute })
      }
    }, [lastRouteMode, location?.pathname, setLastRouteMode])

    return <MemoizedComponent {...props} />
  }
}
